import { z } from "zod";
const HexKey = z.string().regex(/^[0-9a-f]{64}$/);
export const NostrSignedEvent = z
  .object({
    id: HexKey,
    pubkey: HexKey,
    created_at: z.number().int().nonnegative(),
    kind: z.number().int().min(0).max(65535),
    tags: z.array(z.array(z.string().max(2000)).max(8)).max(30),
    content: z.string().max(20000),
    sig: z.string().regex(/^[0-9a-f]{128}$/),
  })
  .strict();
export type NostrEventValue = z.infer<typeof NostrSignedEvent>;
export const AgentIdentityTarget = z
  .object({ botId: z.string().min(1).max(160) })
  .strict();
export const AgentSignedProof = z
  .object({ event: NostrSignedEvent, payload: z.string().max(262144) })
  .strict();
export const AgentIdentityStatus = z.object({
  publicKey: HexKey,
  npub: z.string(),
  version: z.number().int(),
  createdAt: z.string(),
  previous: z.array(
    z.object({
      publicKey: HexKey,
      npub: z.string(),
      version: z.number().int(),
      retiredAt: z.string(),
      transition: AgentSignedProof.nullable(),
    }),
  ),
});
export const AgentIdentityExpected = AgentIdentityTarget.extend({
  expectedPublicKey: HexKey,
});
export const AgentIdentityBackup = z
  .object({
    format: z.literal("wurkees-agent-identity"),
    version: z.literal(1),
    publicKey: HexKey,
    npub: z.string().max(100),
    encryptedKey: z.string().startsWith("ncryptsec1").max(1000),
  })
  .strict();
export const AgentIdentityExport = AgentIdentityExpected.extend({
  passphrase: z.string().min(12).max(256),
}).strict();
export const AgentIdentityImport = AgentIdentityExpected.extend({
  passphrase: z.string().min(1).max(256),
  backup: AgentIdentityBackup,
}).strict();
export type AgentSignedProofValue = z.infer<typeof AgentSignedProof>;
export const AgentIdentityVerify = z
  .object({
    proof: AgentSignedProof,
    expectedPublicKey: z.string().min(1).max(100),
  })
  .strict();
export const AgentIdentityVerification = z.object({
  valid: z.boolean(),
  signatureValid: z.boolean(),
  keyMatches: z.boolean(),
  payloadMatches: z.boolean(),
  publicKey: z.string().nullable(),
  reason: z.string(),
});
export const AgentIdentityProofTarget = z
  .object({
    source: z.enum(["thread", "channel"]),
    messageId: z.string().min(1).max(160),
  })
  .strict();
export const AgentIdentityProofResult = z.object({
  proof: AgentSignedProof,
  verification: AgentIdentityVerification,
  keyStatus: z.enum(["active", "previous"]),
  npub: z.string(),
});
export const AgentIdentityChallenge = AgentIdentityTarget.extend({
  challenge: z.string().min(16).max(2000),
}).strict();
